import { ErrorEnvelopeSchema } from '@bak/types'
import type { z } from 'zod'

import { SERVER_MESSAGE_KEYS, SERVER_MESSAGE_PATTERNS } from '../i18n/messages/common.js'
import { DOMAIN_SERVER_MESSAGE_KEYS } from '../i18n/messages/domains.js'
import { translate, type MessageKey } from '../i18n/index.js'
import { en } from '../i18n/messages/index.js'

/** Typed API failure. `issues` carries field errors for 422 responses. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: { path: (string | number)[]; message: string }[] = [],
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Field path → message, for mapping 422s onto form fields. */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.issues.map((i) => [i.path.join('.'), i.message]))
  }
}

type Options = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  formData?: FormData
  query?: Record<string, string | number | boolean | undefined>
  signal?: AbortSignal
}

/** Listeners run on any 401 so the app can drop to the login screen. */
const unauthorizedListeners = new Set<() => void>()
export const onUnauthorized = (fn: () => void) => {
  unauthorizedListeners.add(fn)
  return () => {
    unauthorizedListeners.delete(fn)
  }
}

async function request(path: string, opts: Options): Promise<Response> {
  const qs = opts.query
    ? '?' +
      new URLSearchParams(
        Object.entries(opts.query).flatMap(([k, v]) => (v === undefined || v === '' ? [] : [[k, String(v)]])),
      ).toString()
    : ''
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'same-origin',
    headers: opts.body !== undefined ? { 'content-type': 'application/json' } : {},
    ...(opts.signal ? { signal: opts.signal } : {}),
  }
  // FormData: leave content-type unset so the browser adds the multipart boundary.
  if (opts.formData) init.body = opts.formData
  else if (opts.body !== undefined) init.body = JSON.stringify(opts.body)

  const res = await fetch(`/api/admin${path}${qs === '?' ? '' : qs}`, init)
  if (!res.ok) {
    const json: unknown = await res.json().catch(() => null)
    const parsed = ErrorEnvelopeSchema.safeParse(json)
    // A failed sign-in step (password, code, set-up before a session) is answered on the sign-in page itself.
    if (res.status === 401 && !path.startsWith('/auth/login') && !path.startsWith('/auth/2fa/verify') && !path.startsWith('/auth/2fa/pending/')) unauthorizedListeners.forEach((fn) => fn())
    if (parsed.success) {
      const issues = Array.isArray(parsed.data.error.issues)
        ? (parsed.data.error.issues as { path: (string | number)[]; message: string }[])
        : []
      throw new ApiError(res.status, parsed.data.error.code, parsed.data.error.message, issues)
    }
    throw new ApiError(res.status, 'internal_error', translate('common.requestFailed', { status: res.status }))
  }
  return res
}

/** JSON call validated against a zod schema. */
export async function api<T extends z.ZodType>(path: string, schema: T, opts: Options = {}): Promise<z.infer<T>> {
  const res = await request(path, opts)
  return schema.parse(await res.json())
}

/** Call with no response body to validate (204 / fire-and-forget). */
export async function apiVoid(path: string, opts: Options = {}): Promise<void> {
  await request(path, opts)
}

/** Message for the user: the API's fixed messages are translated, anything else is shown as sent. */
export const errorMessage = (err: unknown) => {
  const message = err instanceof Error ? err.message : ''
  const key = SERVER_MESSAGE_KEYS[message] ?? DOMAIN_SERVER_MESSAGE_KEYS[message]
  if (key) return translate(key)
  for (const [pattern, patternKey, vars] of SERVER_MESSAGE_PATTERNS) {
    const match = pattern.exec(message)
    if (match) return translate(patternKey, vars(match))
  }
  const feature = /^Your plan does not include (\w+)$/.exec(message)?.[1]
  if (feature && `plan.flag.${feature}` in en) return translate('apiError.planFeature', { feature: translate(`plan.flag.${feature}` as MessageKey).toLowerCase() })
  return message || translate('common.somethingWentWrong')
}
